import type { ButtonHTMLAttributes } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import wacToken from "@/libraries/design/foundations/token/src/wac-token";
import { useDarkTheme } from "@/libraries/design/utils/dark-theme-provider";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

export const LogoButton = ({
    children,
    type = "button",
    ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) => {
    const visualFeedback = checkFeatureFlagGate(
        featureFlags.signupVisualAlignmentFeedback
    );
    const { isDarkTheme } = useDarkTheme();
    return (
        <button
            css={[
                logoButtonCss,
                visualFeedback && visualFeedbackCss,
                isDarkTheme && darkThemeLogoButtonCss
            ]}
            type={type}
            role="link"
            {...rest}
        >
            {children}
        </button>
    );
};

const logoButtonCss = css({
    alignItems: "center",
    backgroundColor: token("elevation.surface"),
    border: `1px solid ${token("color.border")}`,
    borderRadius: 21,
    color: token("color.text.subtle"),
    cursor: "pointer",
    display: "flex",
    fontFamily: "inherit",
    fontSize: "inherit",
    gap: "0.5rem",
    height: "40px",
    justifyContent: "center",
    padding: "0.5rem 0.75rem",
    width: "100%",
    ["&:focus, &:hover"]: {
        backgroundColor: token("elevation.surface.hovered")
    },
    ["&:focus-visible"]: {
        "outline-offset": 1
    }
});

const darkThemeLogoButtonCss = css({
    backgroundColor: wacToken("color.neutral.dark200"),
    border: `1px solid ${wacToken("color.neutral.dark600")}`,
    color: wacToken("color.neutral.light0"),
    ["&:focus, &:hover"]: {
        backgroundColor: wacToken("color.neutral.dark300")
    }
});

const visualFeedbackCss = css({
    borderRadius: "6px !important" // remove !important when gate ships and objects are merged
});
